import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { auth } from '../services/firebase';
import { createUserWithEmailAndPassword, signInWithEmailAndPassword } from 'firebase/auth';

const Auth = ({ mode = 'login' }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    slug: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const isSignup = mode === 'signup';

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isSignup) {
        // Validate slug
        if (!formData.slug || formData.slug.length < 3) {
          throw new Error('Slug must be at least 3 characters long');
        }
        
        // Validate slug format (alphanumeric and hyphens only)
        if (!/^[a-zA-Z0-9-]+$/.test(formData.slug)) {
          throw new Error('Slug can only contain letters, numbers, and hyphens');
        }

        // Create user account
        const userCredential = await createUserWithEmailAndPassword(
          auth, 
          formData.email, 
          formData.password
        );

        // Store slug in sessionStorage for payment return
        sessionStorage.setItem('pendingSlug', formData.slug);
        sessionStorage.setItem('pendingUid', userCredential.user.uid);

        // ========================================
        // STRIPE PAYMENT INTEGRATION
        // ========================================
        // 1. Create Stripe account at stripe.com
        // 2. Products → Add product → Set $2.99/month recurring
        // 3. Create payment link → Copy the link
        // 4. Paste your link below (replace the placeholder)
        // 5. Uncomment the redirect line when ready
        // ========================================
        
        // 👇 PASTE YOUR STRIPE PAYMENT LINK HERE
        const stripePaymentLink = 'https://buy.stripe.com/test_YOUR_LINK_HERE';
        
        // Build checkout URL with pre-filled email and user reference
        const checkoutUrl = `${stripePaymentLink}?prefilled_email=${encodeURIComponent(formData.email)}&client_reference_id=${userCredential.user.uid}`;
        
        // DEMO MODE: Comment out these 3 lines when ready to go live
        alert(`✅ Stripe integration ready!\n\nCheckout URL: ${checkoutUrl}\n\n1. Create your Stripe product\n2. Get payment link\n3. Paste it above\n4. Uncomment redirect below`);
        setTimeout(() => {
          navigate('/dashboard');
        }, 2000);
        
        // PRODUCTION MODE: Uncomment this line to enable real payment redirect
        // window.location.href = checkoutUrl;

      } else {
        // Login
        await signInWithEmailAndPassword(auth, formData.email, formData.password);
        navigate('/dashboard');
      }
    } catch (err) {
      console.error('Auth error:', err);
      
      // User-friendly error messages
      let errorMessage = err.message;
      if (err.code === 'auth/email-already-in-use') {
        errorMessage = 'This email is already registered. Try logging in instead.';
      } else if (err.code === 'auth/weak-password') {
        errorMessage = 'Password should be at least 6 characters long.';
      } else if (err.code === 'auth/invalid-email') {
        errorMessage = 'Please enter a valid email address.';
      } else if (err.code === 'auth/user-not-found' || err.code === 'auth/wrong-password') {
        errorMessage = 'Invalid email or password.';
      } else if (err.code === 'auth/invalid-credential') {
        errorMessage = 'Invalid email or password.';
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-card">
        {/* Logo */}
        <div className="auth-logo">
          <div className="auth-logo-text">
            <span className="auth-logo-icon">R</span>
            <span>RiseMyRank</span>
            <span className="auth-club-stamp">.club</span>
          </div>
        </div>

        {/* Title */}
        <h1 className="auth-title">
          {isSignup ? 'Create Your Account' : 'Welcome Back'}
        </h1>
        <p className="auth-subtitle">
          {isSignup 
            ? 'Join the gold standard in digital business cards' 
            : 'Sign in to manage your digital business card'}
        </p>

        {/* Error Message */}
        {error && (
          <div className="auth-error">
            {error}
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="auth-form">
          {isSignup && (
            <div className="auth-input-group">
              <label className="auth-label" htmlFor="slug">
                Choose Your Handle
              </label>
              <input
                type="text"
                id="slug"
                name="slug"
                className="auth-input"
                placeholder="yourname"
                value={formData.slug}
                onChange={handleChange}
                required={isSignup}
                disabled={loading}
              />
              <p style={{ 
                fontSize: '0.85rem', 
                color: 'var(--white-dim)', 
                marginTop: '0.25rem',
                fontFamily: 'var(--font-mono)'
              }}>
                Your card will be at: risemyrank.club/{formData.slug || 'yourname'}
              </p>
            </div>
          )}

          <div className="auth-input-group">
            <label className="auth-label" htmlFor="email">
              Email
            </label>
            <input
              type="email"
              id="email"
              name="email"
              className="auth-input"
              placeholder="you@example.com"
              value={formData.email}
              onChange={handleChange}
              required
              disabled={loading}
            />
          </div>

          <div className="auth-input-group">
            <label className="auth-label" htmlFor="password">
              Password
            </label>
            <input
              type="password"
              id="password"
              name="password"
              className="auth-input"
              placeholder="••••••••"
              value={formData.password}
              onChange={handleChange}
              required
              disabled={loading}
            />
          </div>

          {isSignup && (
            <div style={{
              background: 'var(--grey-dark)',
              border: '1px solid var(--gold)',
              borderRadius: 'var(--radius-md)',
              padding: 'var(--spacing-md)',
              fontSize: '0.9rem',
              color: 'var(--white-dim)'
            }}>
              <strong style={{ color: 'var(--gold)' }}>Next Step:</strong> After creating your account, 
              you'll be redirected to complete payment ($2.99/month) before accessing your dashboard.
            </div>
          )}

          <button 
            type="submit" 
            className="auth-btn"
            disabled={loading}
          >
            {loading ? (
              <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                <span className="loading-spinner"></span>
                {isSignup ? 'Creating Account...' : 'Signing In...'}
              </span>
            ) : (
              isSignup ? 'Create Account' : 'Sign In'
            )}
          </button>
        </form>

        {/* Toggle Link */}
        <div className="auth-divider">
          {isSignup ? 'Already have an account?' : "Don't have an account?"}
        </div>
        <div className="auth-link">
          <Link to={isSignup ? '/login' : '/signup'}>
            {isSignup ? 'Sign in here' : 'Create one now'}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Auth;
